import React, { useState } from 'react'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'

import getWindow from '../../../../utils/get-window'
import { Radio, RadioGroup, Button, Icon } from '../../../../atoms'
import { CalculatorInput } from '../elements/CalculatorInput'
import { Table, TableRow, TableCell } from '../../../../components/table/platinum-table'
import Trustpilot from './mojo-trustpilot'
import { calculateStampDuty, TaxInterval } from './calculation'

ChartJS.register(ArcElement, Tooltip, Legend)

type UserType = 'Single dwelling' | 'First-time buyer' | 'Moving home' | 'Additional property'
type UserRegion = 'England or Northern Ireland' | 'Scotland' | 'Wales'

export type PropertyDetails = {
  value: number
  type: UserType
  region: UserRegion
}

const CLICKOUT_LINK =
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3Dpartner%26utm_medium%3Duswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dproduct_page&sig=146e8a59474248cfb53133512ba83b5602d023fbcca0ecd2147f64572bd5b12c&brand=uswitch'

const ResultsScreen = ({
  propertyDetails,
  setShowStampDutyPanel,
}: {
  propertyDetails: PropertyDetails
  setShowStampDutyPanel: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const stampDutyCalculation = calculateStampDuty(propertyDetails)
  const { stampDuty, intervals, stampDutyPerInterval = [] } = stampDutyCalculation

  const BreakdownTable = ({
    intervals,
    stampDutyPerInterval,
  }: {
    intervals: any
    stampDutyPerInterval: TaxInterval[]
  }) => {
    const formatNumber = (n: number) => {
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + 'm'
      } else if (n >= 1000) {
        return (n / 1000).toFixed(0) + 'k'
      } else {
        return n ? n?.toString() : null
      }
    }

    return (
      <>
        {stampDutyPerInterval?.length > 0 && (
          <Table footnotes={''} dateString={''}>
            <TableRow>
              <TableCell type={'th'}>Price band</TableCell>
              <TableCell type={'th'}>SDLT %</TableCell>
              <TableCell type={'th'}>Taxable amount</TableCell>
              <TableCell type={'th'}>SDLT payable</TableCell>
            </TableRow>
            {intervals.map((value: { from: number; to: number; rate: number }, key: number) => {
              const { from: rangeFrom, to: rangeTo, rate: rangeRate } = value
              const formattedFrom = formatNumber(rangeFrom)
              const formattedTo = formatNumber(rangeTo)
              const { taxableRange, taxPaid } = stampDutyPerInterval[key] ?? {}

              return (
                <TableRow key={key}>
                  <TableCell type=''>
                    {rangeTo
                      ? `${rangeFrom === 0 ? 'Less than ' : `£${formattedFrom} to `}£${formattedTo}`
                      : `Rest over £${formattedFrom}`}
                  </TableCell>
                  <TableCell type=''>{rangeRate}</TableCell>
                  <TableCell type=''>
                    £{taxableRange?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? 0}
                  </TableCell>
                  <TableCell type=''>
                    £{taxPaid ? taxPaid?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0}
                  </TableCell>
                </TableRow>
              )
            })}
          </Table>
        )}
      </>
    )
  }

  return (
    <div className='StampDutyCalculator__inner-box'>
      <div className='StampDutyCalculator__inner-title'>Results</div>
      <div className='StampDutyCalculator__subtitle'>
        <p>These results are estimated based on the information provided</p>
        <p>The table below shows the breakdown of how much SDLT you will need to pay</p>
      </div>
      <div className='StampDutyCalculator__tax-container'>
        <div className='StampDutyCalculator__tax-container_title'>Total stamp duty to pay</div>
        <div className='StampDutyCalculator__tax-container_value'>
          £{stampDuty?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        </div>
      </div>
      <div className='StampDutyCalculator__table'>
        <BreakdownTable intervals={intervals} stampDutyPerInterval={stampDutyPerInterval} />
      </div>
      <div className='StampDutyCalculator__buttons'>
        <div className='StampDutyCalculator__button-advice'>
          <Button
            style={{ margin: '0, 20px, 0, auto' }}
            variant='primary'
            type='submit'
            withIcon
            href={CLICKOUT_LINK}
          >
            Check out your mortgage options
          </Button>
        </div>
        <div className='StampDutyCalculator__button-recalc'>
          <Button
            style={{ width: '100%' }}
            variant='secondary'
            type='button'
            onClick={() => {
              setShowStampDutyPanel(false)
              const window = getWindow() as Window
              window.scrollTo(0, 0)
            }}
          >
            Recalculate
          </Button>
        </div>
      </div>
      <div className='StampDutyCalculator__logos'>
        <div className='StampDutyCalculator__mojo-logo'>
          <Icon glyph='partnership' ariaLabel={'In partnership with Mojo'} />
        </div>
        <div className='StampDutyCalculator__trustpilot'>
          <Trustpilot theme='light' />
        </div>
      </div>
    </div>
  )
}

const StampDutyForm = ({ onSubmit }: { onSubmit: (submission: PropertyDetails) => void }) => {
  const [propertyValue, setPropertyValue] = useState(0)
  const [userType, setUserType] = useState('First-time buyer')
  const [region, setRegion] = useState('England or Northern Ireland')

  const handleSubmit = (e: any) => {
    e.preventDefault()
    onSubmit({ value: propertyValue, type: userType as UserType, region: region as UserRegion })
  }

  return (
    <div className='StampDutyCalculator__inner-card'>
      Stamp duty calculator
      <div>
        <form className='StampDutyCalculator__inner-container' onSubmit={handleSubmit}>
          <div className='StampDutyCalculator__question-container'>
            <label htmlFor={'calculator_deposit'} className='StampDutyCalculator__panel-label'>
              <span className='StampDutyCalculator__panel-label-title'>
                How much is your property worth?
              </span>
              <div className='StampDutyCalculator__input-wrapper'>
                <CalculatorInput
                  id='propertyValue'
                  name='propertyValue'
                  format='thousands'
                  type='text'
                  placeholder={0}
                  prefix={'£'}
                  onChange={(e: any) => {
                    setPropertyValue(e.target.value)
                  }}
                />
              </div>
            </label>
            <label htmlFor={'describe_situation'} className='StampDutyCalculator__panel-label'>
              <span className='StampDutyCalculator__panel-label-title'>
                What type of buyer are you?
              </span>
              <div className='StampDutyCalculator__radio-group-wrapper'>
                <RadioGroup column={true}>
                  <Radio
                    name='situation'
                    value='First-time buyer'
                    currentValue={userType}
                    onChange={(e: any) => setUserType(e.target.value)}
                    buttonAlignment='left'
                  >
                    First-time buyer
                  </Radio>
                  <Radio
                    name='situation'
                    value='Moving home'
                    currentValue={userType}
                    onChange={(e: any) => setUserType(e.target.value)}
                    buttonAlignment='left'
                  >
                    Moving home
                  </Radio>
                  <Radio
                    name='situation'
                    value='Second property'
                    currentValue={userType === 'Additional property' ? 'Second property' : userType}
                    onChange={() => setUserType('Additional property' as const)}
                    buttonAlignment='left'
                  >
                    Second property
                  </Radio>
                </RadioGroup>
              </div>
            </label>
            <label htmlFor={'describe_situation'} className='StampDutyCalculator__panel-label'>
              <span className='StampDutyCalculator__panel-label-title'>
                Where is the property located?
              </span>
              <div className='StampDutyCalculator__radio-group-wrapper'>
                <RadioGroup column={true}>
                  <Radio
                    name='region'
                    value='England or Northern Ireland'
                    currentValue={region}
                    onChange={(e: any) => setRegion(e.target.value)}
                    buttonAlignment='left'
                  >
                    England or Northern Ireland
                  </Radio>
                  <Radio
                    name='region'
                    value='Scotland'
                    currentValue={region}
                    onChange={(e: any) => setRegion(e.target.value)}
                    buttonAlignment='left'
                  >
                    Scotland
                  </Radio>
                  <Radio
                    name='region'
                    value='Wales'
                    currentValue={region}
                    onChange={(e: any) => setRegion(e.target.value)}
                    buttonAlignment='left'
                  >
                    Wales
                  </Radio>
                </RadioGroup>
              </div>
            </label>
          </div>
          <div className='StampDutyCalculator__footer-container'>
            <Button withIcon variant='primary' type='submit'>
              Calculate
            </Button>

            <div className='StampDutyCalculator__logos'>
              <div className='StampDutyCalculator__mojo-logo'>
                <Icon glyph='partnership' ariaLabel={'In partnership with Mojo'} />
              </div>
              <div className='StampDutyCalculator__trustpilot'>
                <Trustpilot theme='light' />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

const StampDutyCalculator = () => {
  const [showStampDutyPanel, setShowStampDutyPanel] = useState(false)
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails>({
    value: 200000,
    type: 'First-time buyer',
    region: 'England or Northern Ireland',
  })

  const handleFormSubmit = (submission: PropertyDetails) => {
    setPropertyDetails(submission)
    setShowStampDutyPanel(true)
  }

  return (
    <div className='StampDutyCalculator'>
      <script
        type='text/javascript'
        src='//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
        async
      />
      <div className='StampDutyCalculator__gauge-card StampDutyCalculator__main-content-container'>
        <div className='StampDutyCalculator__content-container'>
          <div className='StampDutyCalculator__left-side'>
            <h1 className='StampDutyCalculator__heading'>Stamp duty calculator</h1>
            <p className='StampDutyCalculator__paragraph'>
              Find out how much stamp duty land tax (SDLT) you have to pay.
            </p>
          </div>
          {showStampDutyPanel ? (
            <ResultsScreen
              propertyDetails={propertyDetails}
              setShowStampDutyPanel={setShowStampDutyPanel}
            />
          ) : (
            <StampDutyForm onSubmit={handleFormSubmit} />
          )}
        </div>
      </div>
    </div>
  )
}

export default StampDutyCalculator
